import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cardsData } from './CarouselCards';
import { FaPlay } from 'react-icons/fa';
import { API_CONFIG } from '../config/envConfig';

export default function MobileCategoryUI() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [videoData, setVideoData] = useState(null);
  const videoRefs = useRef({});
  
  // Get cardId from URL params, default to first card
  const cardId = parseInt(searchParams.get('cardId') || '1');
  
  const currentCard = cardsData.find(card => card.id === cardId) || cardsData[0];
  const currentCardIndex = cardsData.findIndex(card => card.id === cardId);
  
  // Get previous and next cards
  const prevCard = currentCardIndex > 0 ? cardsData[currentCardIndex - 1] : null;
  const nextCard = currentCardIndex < cardsData.length - 1 ? cardsData[currentCardIndex + 1] : null;

  // Keep page scrolled to top when arriving or changing cards
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [cardId]);

  // Fetch video data from API on component mount/refresh
  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        const response = await fetch(API_CONFIG.listFolderVideosUrl)
        const data = await response.json()
        setVideoData(data)
        console.log('Video data fetched:', data)
      } catch (error) {
        console.error('Error fetching video data:', error)
      }
    }

    fetchVideoData()
  }, [])

  // Calculate global subCard index for video link mapping
  // This matches the logic in CarouselCards.js
  const getGlobalSubCardIndex = (cardId, subCardIndex) => {
    // Find the card index (0-based) from cardId
    const cardIndex = cardsData.findIndex(card => card.id === cardId)
    if (cardIndex === -1) return 0
    
    let globalIndex = 0
    // Sum up subCards from all previous cards
    for (let i = 0; i < cardIndex; i++) {
      globalIndex += cardsData[i].subCards.length
    }
    return globalIndex + subCardIndex
  }

  // Get video link for a specific subCard
  const getVideoLink = (cardId, subCardIndex) => {
    if (!videoData) return null
    const globalIndex = getGlobalSubCardIndex(cardId, subCardIndex)
    const linkNumber = globalIndex + 1 // link1, link2, etc. (1-indexed)
    const linkKey = `link${linkNumber}`
    const videoLink = videoData[linkKey] || null
    
    // Debug logging for Win card (cardId = 3)
    // if (cardId === 3) {
    //   console.log(`Win card - subCardIndex: ${subCardIndex}, globalIndex: ${globalIndex}, linkNumber: ${linkNumber}, linkKey: ${linkKey}, videoLink: ${videoLink ? 'exists' : 'null'}`)
    // }
    
    return videoLink
  }

  // Track currently playing video
  const currentlyPlayingRef = useRef(null)

  // Auto-play videos when they come into view
  useEffect(() => {
    if (!videoData) return

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Trigger when 50% of video is visible
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target
        if (entry.isIntersecting && video) {
          // Pause and reset the previously playing video
          if (currentlyPlayingRef.current && currentlyPlayingRef.current !== video) {
            currentlyPlayingRef.current.pause()
            currentlyPlayingRef.current.currentTime = 0
          }
          // Reset and play the current video
          video.currentTime = 0
          video.play().catch(err => {
            console.log('Auto-play prevented:', err)
          })
          currentlyPlayingRef.current = video
        } else if (!entry.isIntersecting && video && !video.paused) {
          // Pause and reset when video goes out of view
          video.pause()
          video.currentTime = 0
          if (currentlyPlayingRef.current === video) {
            currentlyPlayingRef.current = null
          }
        }
      })
    }, observerOptions)

    // Observe all video elements
    const currentRefs = videoRefs.current
    Object.values(currentRefs).forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => {
      Object.values(currentRefs).forEach((ref) => {
        if (ref) observer.unobserve(ref)
      })
    }
  }, [videoData, currentCard])
  
  return (
    <div className="min-h-screen bg-white px-6 pb-6" style={{ paddingTop: '7.5rem' }}>
      {/* Breadcrumb with Back Button */}
      <div className="fixed top-12 left-0 right-0 z-30 bg-white flex items-center gap-2 py-3 px-6 border-b border-gray-100 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-700 hover:text-gray-900 transition-colors"
          aria-label="Go back"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div className="flex items-center text-base" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          <span className="text-gray-700 font-normal">Home /&nbsp;</span>
          <span className="font-semibold" style={{ color: '#3250FD' }}>{currentCard.title}</span>
        </div>
      </div>

      {/* First Slide Questions (only show bullets if it's the first subCard) */}
      {currentCard.subCards[0]?.bullets && currentCard.subCards[0].bullets.length > 0 && (
        <div className="mb-8 space-y-6">
          {currentCard.subCards[0].bullets.map((bullet, index) => (
            <p
              key={index}
              className="text-gray-800 text-lg leading-relaxed"
              style={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 500,
              }}
            >
              {bullet}
            </p>
          ))}
        </div>
      )}

      {/* Display all subCards for the current card */}
      {currentCard.subCards
        .filter(subCard => subCard.title !== 'Track Signals')
        .map((subCard, index) => {
          // Check if this is the first subCard in the original array
          const originalIndex = currentCard.subCards.findIndex(sc => sc.id === subCard.id);
          const isFirstSubCard = originalIndex === 0;
          
          return (
            <div key={subCard.id} className="mb-12">
              {/* Skip first subCard video */}
              {!isFirstSubCard && (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5 md:gap-6">
                  {/* Left: Title and Description - Smaller width */}
                  <div className="md:col-span-4 space-y-4">
                    {/* Title */}
                    {subCard.title && (
                      <h2
                        className="text-2xl sm:text-3xl font-bold leading-tight"
                        style={{
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                          fontWeight: 700,
                          color: '#667EEA',
                        }}
                      >
                        {subCard.title}
                      </h2>
                    )}

                    {/* Description */}
                    {subCard.content && (
                      <div className="space-y-4">
                        <p
                          className="text-gray-600 text-sm sm:text-base leading-relaxed"
                          style={{
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            fontWeight: 400,
                          }}
                        >
                          {subCard.content}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right: Video - Larger width, full width and height auto */}
                  <div className="md:col-span-8">
                    <VideoPlayer
                      videoLink={getVideoLink(currentCard.id, originalIndex)}
                      subCardId={subCard.id}
                      videoRefs={videoRefs}
                    />
                  </div>
                </div>
              )}

              {/* First subCard: Only show title and description */}
              {isFirstSubCard && (
                <div className="space-y-4">
                  {/* Title */}
                  {subCard.title && (
                    <h2
                      className="text-2xl sm:text-3xl font-bold leading-tight"
                      style={{
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 700,
                        color: '#667EEA',
                      }}
                    >
                      {subCard.title}
                    </h2>
                  )}

                  {/* Description */}
                  {subCard.content && (
                    <div className="space-y-4">
                      <p
                        className="text-gray-600 text-sm sm:text-base leading-relaxed"
                        style={{
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        {subCard.content}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
      })}

      {/* Navigation Buttons at End of Scroll - Show Only Previous and Next */}
      {(prevCard || nextCard) && (
        <div className="mt-16 mb-8 px-6">
          <div className="flex flex-col gap-3 max-w-md mx-auto">
            {/* Previous Button */}
            {prevCard && (
              <button
                onClick={() => {
                  setSearchParams({
                    cardId: prevCard.id.toString()
                  });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90"
                style={{
                  backgroundColor: prevCard.bgColor,
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  <div className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-sm font-semibold">
                    {prevCard.step}
                  </div>
                  <span>{prevCard.title}</span>
                </div>
              </button>
            )}
            
            {/* Next Button */}
            {nextCard && (
              <button
                onClick={() => {
                  setSearchParams({
                    cardId: nextCard.id.toString()
                  });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90"
                style={{
                  backgroundColor: nextCard.bgColor,
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-sm font-semibold">
                    {nextCard.step}
                  </div>
                  <span>{nextCard.title}</span>
                </div>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Video Player Component
function VideoPlayer({ videoLink, subCardId, videoRefs }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef(null)

  // Store ref in the parent's refs object
  useEffect(() => {
    const currentRefs = videoRefs.current
    if (videoRef.current) {
      currentRefs[subCardId] = videoRef.current
    }
    return () => {
      delete currentRefs[subCardId]
    }
  }, [subCardId, videoRefs])

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  return (
    <div className="relative bg-[#1a1a1a] overflow-hidden w-full aspect-video">
      {videoLink ? (
        <>
          <video
            ref={videoRef}
            src={videoLink}
            className="w-full h-full object-cover"
            loop
            muted
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onLoadedMetadata={(e) => {
              // Show first frame by seeking to 0
              e.target.currentTime = 0.1
            }}
          />
          {/* Play Button Overlay - Only show icon */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={handlePlayClick}
                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all group shadow-lg"
              >
                <FaPlay className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white ml-1 sm:ml-1.5 md:ml-2 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          )}
          {/* Pause button when playing */}
          {isPlaying && (
            <button
              onClick={handlePlayClick}
              className="absolute inset-0 flex items-center justify-center bg-transparent hover:bg-black/20 transition-all cursor-pointer opacity-0 hover:opacity-100"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center">
                  <div className="w-2 h-8 sm:h-10 md:h-12 bg-white rounded-sm mr-1"></div>
                  <div className="w-2 h-8 sm:h-10 md:h-12 bg-white rounded-sm"></div>
                </div>
              </div>
            </button>
          )}
        </>
      ) : (
        <>
          {/* Fallback placeholder if no video link */}
          <div className="relative w-full h-full flex items-center justify-center bg-[#1a1a1a]">
            <button className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all group shadow-lg">
              <FaPlay className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white ml-1 sm:ml-1.5 md:ml-2 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </>
      )}
    </div>
  )
}
